import { datadogRum } from '@datadog/browser-rum'
import { HelpOutline, KeyboardArrowDown, Chat, Logout, Settings } from '@mui/icons-material'
import { Badge, Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material'
import Link from 'next/link'
import { signOut } from 'next-auth/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getTargetOrg, setTargetOrg } from '@omnea-app/fe-auth/TokenProvider'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { LoggedInUserAvatar } from '@omnea-app/fe-users-and-groups/avatar/UserAvatar'
import { BEAMER_TRIGGER_ID } from '@omnea-app/frontend/components/layout/wrapper/beamer'
import { useIsBeamerEnabled } from '@omnea-app/frontend/components/layout/wrapper/useIsBeamerEnabled'
import { CopyIdButton } from '@omnea-app/frontend/components/shared/ui/CopyIdButton/CopyIdButton'
import { NotificationInbox } from './NotificationInbox'
import { OrganisationIndicator } from './OrganisationIndicator/OrganisationIndicator'
import { useBeamerUnreadCount } from './useBeamerUnreadCount'

type UserDetailsView = 'full' | 'compact' | 'minimal'

interface UserDetailsProps {
  view?: UserDetailsView
}

export const UserDetails = ({ view = 'full' }: UserDetailsProps) => {
  const fullView = view === 'full'
  const minimalView = view === 'minimal'

  const userData = useAuthenticatedUser()
  const [helpMenuAnchorEl, setHelpMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLElement | null>(null)
  const { t } = useTranslation('common')
  const isBeamerEnabled = useIsBeamerEnabled()
  const { unreadCount: beamerUnreadCount, refetchUnreadCount } = useBeamerUnreadCount({
    enabled: isBeamerEnabled,
  })

  if (!userData.isFullyAuthenticated || !userData.user) {
    return null
  }

  const { user } = userData // Reading the `user` property from the `userData` will throw an error if `isFullyAuthenticated` is false

  const hasBeamerUnread = beamerUnreadCount > 0

  const openUserMenu = (event: React.MouseEvent<HTMLElement>) => setUserMenuAnchorEl(event.currentTarget)
  const closeUserMenu = () => setUserMenuAnchorEl(null)

  const openHelpMenu = (event: React.MouseEvent<HTMLElement>) => {
    setHelpMenuAnchorEl(event.currentTarget)
  }
  const closeHelpMenu = () => setHelpMenuAnchorEl(null)

  const handleLogout = () => {
    const targetOrg = getTargetOrg()
    try {
      localStorage.clear()
    } catch {
      // Ignore storage errors and continue with sign out
    } finally {
      if (targetOrg) setTargetOrg(targetOrg)
      void signOut({ callbackUrl: '/login' })
    }
  }

  const showPylon = () => {
    window.Pylon('setNewIssueCustomFields', {
      user_id: user.id,
      user_email: user.email,
      user_role: user.role,
      user_is_view_only: user.isViewOnly,
      user_has_config_permissions: user.hasConfigPermissions,
      organisation_name: userData.org.name,
      organisation_id: userData.org.organisationId,
      session_url: datadogRum.getSessionReplayLink(),
    })
    window.Pylon('show')
  }

  const showBeamer = () => {
    if (window.Beamer) {
      window.Beamer.update({
        user_id: user.id,
        user_firstname: user.firstName,
        user_lastname: user.lastName,
        user_email: user.email,
        onclose: refetchUnreadCount,
      })
      window.Beamer.show()
    }
  }

  const showSettingsLink = !user.isSupplier

  return (
    <Stack spacing={4} direction="row" alignItems="center">
      {!minimalView && (
        <>
          <CopyIdButton id={userData.org.organisationId} entityTypeLabel="organisation" />
          <NotificationInbox />
        </>
      )}
      <IconButton
        sx={{
          color: 'neutralLight.900',
          '&.MuiButtonBase-root:hover': {
            bgcolor: 'transparent',
          },
        }}
        onClick={openHelpMenu}
      >
        <Box
          component="span"
          id={BEAMER_TRIGGER_ID}
          sx={{
            position: 'absolute',
            top: 4,
            ...(view === 'compact' ? { right: 6 } : { left: 30 }),
            width: 0,
            height: 0,
            pointerEvents: 'none',
            '& .beamer_icon.active': {
              backgroundColor: (theme) => `${theme.palette.green['500']} !important`,
              pointerEvents: 'none !important',
              borderRadius: '100% !important',
              width: '8px !important',
              height: '8px !important',
              padding: '0 !important',
              overflow: 'hidden !important',
              fontSize: '0 !important',
              outline: (theme) => `2px solid ${theme.palette.neutralLight['100']} !important`,
              animation: 'inbox--badge-pulse 10s infinite !important',
            },
          }}
        />
        <HelpOutline sx={{ width: 23, height: 23, mr: fullView ? 1 : 0 }} />
        {fullView && (
          <Typography variant="Body14pxRegular">{isBeamerEnabled ? t('header.help-and-updates') : t('header.help')}</Typography>
        )}
      </IconButton>
      <Menu
        open={Boolean(helpMenuAnchorEl)}
        anchorEl={helpMenuAnchorEl}
        onClose={closeHelpMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            closeHelpMenu()
            showPylon()
          }}
        >
          <ListItemIcon>
            <Chat />
          </ListItemIcon>
          <ListItemText>{t('header.support')}</ListItemText>
        </MenuItem>
        {isBeamerEnabled && (
          <MenuItem
            onClick={() => {
              closeHelpMenu()
              showBeamer()
            }}
          >
            <ListItemIcon>
              <Badge
                variant="dot"
                invisible={!hasBeamerUnread}
                sx={{ '& .MuiBadge-dot': { bgcolor: 'green.500', top: 4, right: 4, outline: '2px solid white' } }}
              >
                <HelpOutline />
              </Badge>
            </ListItemIcon>
            <ListItemText>{t('header.product-updates')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
      <Stack
        direction="row"
        alignItems="center"
        alignSelf="stretch"
        sx={{
          cursor: 'pointer',
          minWidth: view === 'compact' ? 40 : 'auto',
        }}
        onClick={openUserMenu}
      >
        <Tooltip title={user.email}>
          {/* Wrapped in div so the tooltip can pass refs */}
          <div>
            <LoggedInUserAvatar user={user} />
          </div>
        </Tooltip>
        {fullView && (
          <Typography variant="Body14pxRegular" color="neutralLight.900" sx={{ ml: 2 }}>
            {user.firstName + ' ' + user.lastName}
          </Typography>
        )}
        {fullView && <KeyboardArrowDown sx={{ color: 'neutralLight.700', fontSize: 20 }} data-testid="user-menu" />}
      </Stack>
      <Menu
        open={Boolean(userMenuAnchorEl)}
        anchorEl={userMenuAnchorEl}
        onClose={closeUserMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 200, mt: 1 } } }}
      >
        {showSettingsLink && (
          <MenuItem component={Link} href="/settings/notifications" onClick={closeUserMenu}>
            <ListItemIcon>
              <Settings />
            </ListItemIcon>
            <ListItemText>{t('header.settings')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            closeUserMenu()
            handleLogout()
          }}
        >
          <ListItemIcon>
            <Logout />
          </ListItemIcon>
          <ListItemText>{t('header.logout')}</ListItemText>
        </MenuItem>
      </Menu>
      {!minimalView && <OrganisationIndicator />}
    </Stack>
  )
}
